import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Loader2, Crown, Swords, Flame, Heart } from "lucide-react";

const medal = (i) => ["text-yellow-400", "text-zinc-300", "text-amber-600"][i] || "text-foreground/40";

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const Row = ({ rank, children }) => (
    <div className="flex items-center gap-4 border border-border bg-card px-5 py-4 transition-colors hover:border-primary/40">
      <span className={`w-8 text-center font-display text-2xl ${medal(rank)}`}>{rank + 1}</span>
      {children}
    </div>
  );

  return (
    <div data-testid="leaderboard-page" className="mx-auto max-w-4xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Trophy className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.25em]">Leaderboard</span>
      </div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">Hall of <span className="text-primary">Survivors</span></h1>
      <p className="mt-2 text-foreground/60">The fog remembers who endures.</p>

      <Tabs defaultValue="creators" className="mt-8">
        <TabsList className="grid w-full grid-cols-3 rounded-sm border border-white/10 bg-card p-1">
          <TabsTrigger data-testid="tab-creators" value="creators" className="rounded-sm text-xs font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Crown className="mr-1.5 h-4 w-4" />Top Creators</TabsTrigger>
          <TabsTrigger data-testid="tab-challenges" value="challenges" className="rounded-sm text-xs font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Swords className="mr-1.5 h-4 w-4" />Challenges</TabsTrigger>
          <TabsTrigger data-testid="tab-ideas" value="ideas" className="rounded-sm text-xs font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Flame className="mr-1.5 h-4 w-4" />Hot Ideas</TabsTrigger>
        </TabsList>

        <TabsContent value="creators" className="mt-5 space-y-3">
          {data.top_creators.length === 0 ? <Empty /> : data.top_creators.map((u, i) => (
            <Row key={u.user_id} rank={i}>
              <Link to={`/u/${u.username}`} className="flex flex-1 items-center justify-between">
                <div>
                  <p className="font-bold">{u.username}</p>
                  <p className="text-xs uppercase tracking-widest text-primary">{u.rank}</p>
                </div>
                <span className="font-display text-2xl text-primary">{u.score}<span className="ml-1 text-xs text-foreground/40">pts</span></span>
              </Link>
            </Row>
          ))}
        </TabsContent>

        <TabsContent value="challenges" className="mt-5 space-y-3">
          {data.top_challenges.length === 0 ? <Empty /> : data.top_challenges.map((u, i) => (
            <Row key={u.user_id} rank={i}>
              <Link to={`/u/${u.username}`} className="flex flex-1 items-center justify-between">
                <p className="font-bold">{u.username}</p>
                <span className="flex items-center gap-2 font-display text-2xl text-primary"><Swords className="h-5 w-5" />{u.challenges_completed}</span>
              </Link>
            </Row>
          ))}
        </TabsContent>

        <TabsContent value="ideas" className="mt-5 space-y-3">
          {data.popular_ideas.length === 0 ? <Empty /> : data.popular_ideas.map((p, i) => (
            <Row key={p.id} rank={i}>
              <div className="flex flex-1 items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate font-bold">{p.title}</p>
                  <Link to={`/u/${p.username}`} className="text-xs uppercase tracking-widest text-foreground/50 hover:text-primary">@{p.username} · {p.type}</Link>
                </div>
                <span className="flex shrink-0 items-center gap-2 font-display text-2xl text-primary"><Heart className="h-5 w-5 fill-primary" />{p.likes}</span>
              </div>
            </Row>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = () => <div className="border border-dashed border-white/10 py-16 text-center text-foreground/50">No data yet. Start creating!</div>;
